import { useCallback, useEffect, useState } from 'react';
import { Background, Controls, MiniMap, Panel, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
  Edge,
  EdgeMouseHandler,
  Node,
  NodeMouseHandler,
} from '@xyflow/react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SettingsIcon from '@mui/icons-material/Settings';

import { PaletteModeEnum } from '@e-socna/data-layer';

import { useUiSettingsContext } from '../../contexts/UiSettingsContext/hooks/useUiSettingsContext.hook';

import { buildWorkflowGraph } from './utils/build-workflow-graph.util';
import { WorkflowEdge } from './WorkflowEdge';
import { WorkflowStateNode } from './WorkflowStateNode';
import type {
  WorkflowEdgeData,
  WorkflowGraphSource,
  WorkflowNodeData,
  WorkflowSelection,
  WorkflowStateLookup,
} from './utils/build-workflow-graph.util';

const NODE_TYPES = { workflowState: WorkflowStateNode };
const EDGE_TYPES = { workflowEdge: WorkflowEdge };

interface Props {
  readonly getState: WorkflowStateLookup;
  readonly onSelect: (selection: Readonly<WorkflowSelection>) => void;
  readonly workflow: Readonly<WorkflowGraphSource & { label: string }>;
}

interface Graph {
  readonly nodes: readonly Node<WorkflowNodeData>[];
  readonly edges: readonly Edge<WorkflowEdgeData>[];
}

export const WorkflowGraph = ({
  getState,
  onSelect,
  workflow,
}: Readonly<Props>) => {
  const [graph, setGraph] = useState<Graph | null>(null);
  const { paletteMode } = useUiSettingsContext();

  useEffect(() => {
    // eslint-disable-next-line functional/no-let -- standard React cancellation-guard pattern for an async effect
    let isCurrent = true;

    void buildWorkflowGraph(workflow, getState).then((builtGraph) => {
      if (!isCurrent) {
        return;
      }

      setGraph(builtGraph);
    });

    return () => {
      isCurrent = false;
    };
  }, [workflow, getState]);

  const handleWorkflowSettingsClick = useCallback(() => {
    onSelect({ kind: 'workflow', raw: workflow, title: workflow.label });
  }, [onSelect, workflow]);

  const handleEdgeClick: EdgeMouseHandler<Edge<WorkflowEdgeData>> = useCallback(
    (_event, edge) => {
      const label = typeof edge.label === 'string' ? edge.label : '';

      onSelect({
        kind: 'action',
        raw: edge.data?.raw,
        title: `Action : ${label}`,
      });
    },
    [onSelect],
  );

  const handleNodeClick: NodeMouseHandler<Node<WorkflowNodeData>> = useCallback(
    (_event, node) => {
      onSelect({
        kind: 'node',
        raw: node.data.raw,
        title: node.data.label,
      });
    },
    [onSelect],
  );

  if (!graph) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: 600,
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <ReactFlow
        colorMode={paletteMode === PaletteModeEnum.Dark ? 'dark' : 'light'}
        edgeTypes={EDGE_TYPES}
        edges={graph.edges as Edge<WorkflowEdgeData>[]}
        fitView
        nodeTypes={NODE_TYPES}
        nodes={graph.nodes as Node<WorkflowNodeData>[]}
        onEdgeClick={handleEdgeClick}
        onNodeClick={handleNodeClick}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <MiniMap pannable zoomable />
        <Panel position="top-left">
          <Button
            onClick={handleWorkflowSettingsClick}
            size="small"
            startIcon={<SettingsIcon />}
            variant="outlined"
          >
            Paramètres du workflow
          </Button>
        </Panel>
      </ReactFlow>
    </Box>
  );
};
